// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — Hall3D.jsx   ·   THE HALL OF DEEDS, IN THREE DIMENSIONS
// ────────────────────────────────────────────────────────────────────
// ⭐ THIS IS A RENDERER, NOT A GAME.
//
// PierHome.js keeps every rule: the map, the wings, the trophy grid, the
// frame slots, the Deed Book, the yard door, furnishing, presence and every
// Firebase write. This file is handed that data and the position refs and
// paints the inside of the portico that stands on the boardwalk. Swapping
// back to the 2D hall is `is3D` in PierHome — one boolean.
//
// THE LAWS, inherited from Pier3D and Minerium3D, none optional:
//  1 · THREE LIGHTS. Every sconce, spot, window and doorway is emissive,
//      basic or additive, or a pool painted on the stone.
//  2 · PLAIN BILLBOARD <Html center> ONLY, and only for the 2D sprites
//      (terror-assets sends no CORS headers). `<Html transform>` renders
//      nothing on caP'n's machine. Names and plaques are canvas meshes.
//  3 · MOVEMENT NEVER RE-RENDERS THIS TREE. Positions arrive by ref and
//      are read in useFrame; the host is React.memo'd.
//  4 · REACT CONTEXT DOES NOT CROSS <Canvas>. The camera talks through
//      pierCamera.js; the cut-away through a module registry.
//
// ⚑ THE ONE SCALE is `S` in hallBuild.jsx: world units per hall tile.
// Geometry and sprites both go through it; PierHome's map does not change.
// ════════════════════════════════════════════════════════════════════

import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { getPlayerSprite, getIdleSprite, getCompanionSprite, PLAYER_SPRITES } from "./SpriteManifest";
import { textTex, useFontGen, releaseTextures, TEX, canvasTex as canvasTexP, mulberry32 } from "./pierTex";
import { shadowTex } from "./pierPaint";
import { BuildDriver } from "./pierBuild";
import {
  getView, viewMult, isPosed, setPosed, resetToken, subscribe,
  setGroundForward, resetGroundForward,
} from "./pierCamera";
// ⚑ The same bar in the same corner as the boardwalk and the yard. This
// extends the module cycle Minerium3D already documents; safe because
// CameraBar is a hoisted `export function` never called at module scope.
import { CameraBar } from "./Pier3D";
import { MineriumProp, releasePropMaterials } from "./mineriumProps";
import { ITEMS, KIND, LAYER, layerOf } from "./mineriumCatalogue";
import { resolvePlacement, growthOf, serverNow, dimsOf } from "./minerium";
import { UI } from "./theme";
import { burstStyle, GHOST_VERB } from "./buildKit";
import {
  S, HallShell, FloorDeeds, WallDeeds, GalleryFrames, DeedBook,
  deedAtlas, driveCuts, roomOf, CUTS,
} from "./hallBuild";
import { InteriorWalls, FloorPaint } from "./hallInteriorBuild";

// ⭐ THE PIER'S SCALE for people: 48px of art at distanceFactor 12 is one
// unit, so a 72px sprite stands 1.5 units tall — as on the boardwalk.
const ART_TILE = 48;
const DF_TILE = 12;

const C = { nullus: "#B388FF" };
const IDLE_ART = PLAYER_SPRITES.player_f_idle_a !== PLAYER_SPRITES.player_f_down_a;

// ════════════════════════════════════════════════════════════════════
// THE PEOPLE — the real 2D sprites, standing on the marble
// ════════════════════════════════════════════════════════════════════
function SpriteImg({ src, gender, size, glow }) {
  // silence beats a broken box (Pier3D, 2026-08-25)
  const [failed, setFailed] = useState(false);
  const [fallback, setFallback] = useState(false);
  const alt = PLAYER_SPRITES[`player_${gender === "f" ? "f" : "m"}_default`];
  useEffect(() => { setFailed(false); setFallback(false); }, [src]);
  if (!src || failed) return null;
  return (
    <img src={fallback && alt ? alt : src} alt="" draggable={false}
      onError={() => (fallback || !alt ? setFailed(true) : setFallback(true))}
      style={{ width: size, height: size, imageRendering: "pixelated", filter: glow || "none", display: "block" }} />
  );
}

const camRelativeDir = (fx, fz, camYaw) => {
  const a = Math.atan2(fx, fz) - camYaw;
  const q = Math.round(a / (Math.PI / 2)) & 3;
  // ⚠ q = 1 is facing +x (screen right, with the lens south of you), so it
  // is the RIGHT sprite. The old table had the side views swapped: every step
  // east showed the left-facing sprite (code review, confirmed live).
  return [{ x: 0, y: 1 }, { x: 1, y: 0 }, { x: 0, y: -1 }, { x: -1, y: 0 }][q];
};

const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();

// A painted name, a FULL billboard (Pier3D's PaintedSign rule), drawn over
// the plinths so a trophy never swallows the name of the player beside it.
function NamePlate({ text, color, y }) {
  const ref = useRef();
  const gen = useFontGen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tex = useMemo(() => textTex(text, color), [text, color, gen]);
  const h = 0.34, w = h * (tex.userData.aspect || 4);
  useFrame(({ camera }) => {
    const o = ref.current; if (!o) return;
    o.parent?.getWorldQuaternion(_q);
    o.quaternion.copy(_q.invert()).multiply(camera.quaternion);
    // ⚠ capped by distance: with no depth test, a world-sized plate close to
    // the lens grew huge and hung in front of everything (visual review)
    o.getWorldPosition(_v);
    o.scale.setScalar(Math.min(1, camera.position.distanceTo(_v) / 7));
  });
  return (
    <mesh ref={ref} position={[0, y, 0]} renderOrder={10}>
      <planeGeometry args={[w, h]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} depthTest={false} toneMapped={false} fog={false} />
    </mesh>
  );
}

// ⚠ PierHome walks a whole tile every 150ms. The actor GLIDES to each new
// tile at that same pace, so a step reads as a walk, never a hop; a jump
// of several tiles (a teleport, a late presence tick) snaps.
const GLIDE = (S / 0.15) * 1.1;

function Actor({ read, gender, companionId, name, tint, isLocal, outRef }) {
  const g = useRef();
  const compRef = useRef();
  const [look, setLook] = useState({ dx: 0, dy: 1, moving: false, idleB: false });
  const lookRef = useRef(look);
  const start = read() || { x: 0, y: 0 };
  const initPos = useRef([start.x * S, 0, start.y * S]);
  const lastP = useRef({ x: initPos.current[0], z: initPos.current[2] });
  const acc = useRef(0);

  useFrame(({ camera, clock }, dt) => {
    const o = g.current; if (!o) return;
    const p = read(); if (!p) return;
    const tx = p.x * S, tz = p.y * S;
    const dx = tx - o.position.x, dz = tz - o.position.z;
    const d = Math.hypot(dx, dz);
    const step = GLIDE * Math.min(dt || 0.016, 0.05);
    if (d > S * 4 || d <= step) { o.position.x = tx; o.position.z = tz; }
    else { o.position.x += (dx / d) * step; o.position.z += (dz / d) * step; }
    if (outRef) { outRef.current.x = o.position.x; outRef.current.z = o.position.z; }
    // the companion walks at heel on the FAR side of its owner from the lens.
    // A fixed world offset put it over her head from one side and across her
    // from the other (second visual review). Right of the view, a step behind.
    const cg = compRef.current;
    if (cg) {
      let fx = o.position.x - camera.position.x, fz = o.position.z - camera.position.z;
      const fl = Math.hypot(fx, fz) || 1; fx /= fl; fz /= fl;
      cg.position.set(-fz * 0.72 + fx * 0.42, 0, fx * 0.72 + fz * 0.42);
    }

    acc.current += dt || 0.016;
    if (acc.current < 0.14) return;
    const moved = Math.hypot(o.position.x - lastP.current.x, o.position.z - lastP.current.z) > 0.05 || d > 0.05;
    acc.current = 0;
    lastP.current = { x: o.position.x, z: o.position.z };
    const camYaw = Math.atan2(camera.position.x - o.position.x, camera.position.z - o.position.z);
    const nd = camRelativeDir(p.fx || 0, p.fy || (p.fx ? 0 : 1), camYaw);
    const idleB = Math.floor(clock.elapsedTime / 1.7) % 2 === 1;
    const cur = lookRef.current;
    if (nd.x !== cur.dx || nd.y !== cur.dy || moved !== cur.moving || (IDLE_ART && !moved && idleB !== cur.idleB)) {
      const next = { dx: nd.x, dy: nd.y, moving: moved, idleB };
      lookRef.current = next;
      setLook(next);
    }
  });

  const src = look.moving || !IDLE_ART ? getPlayerSprite(gender, look.dx, look.dy, false) : getIdleSprite(gender, look.idleB);
  const comp = companionId ? getCompanionSprite(companionId) : null;
  const shadow = useMemo(() => new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, opacity: 0.8, depthWrite: false }), []);
  useEffect(() => () => shadow.dispose(), [shadow]);

  return (
    <group ref={g} position={initPos.current}>
      {/* the contact shadow that stands them ON the marble */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} material={shadow} renderOrder={2}>
        <planeGeometry args={[1.0, 0.8]} />
      </mesh>
      <Html center distanceFactor={DF_TILE} position={[0, 0.78, 0]} zIndexRange={[8, 0]} style={{ pointerEvents: "none" }}>
        <SpriteImg src={src} gender={gender} size={ART_TILE * 1.5} glow={isLocal ? "drop-shadow(0 0 10px #c9a84c55)" : null} />
      </Html>
      {/* ⚠ the companion walks at heel: smaller than its owner and a step
          behind, on its own shadow. At the owner's height it upstaged the
          player (visual reviews of the pier and the Hall, 2026-09-14). */}
      {comp && (
        <>
          <group ref={compRef} position={[0.72, 0, -0.42]}>
            <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} material={shadow} renderOrder={2}>
              <planeGeometry args={[0.85, 0.65]} />
            </mesh>
            <Html center distanceFactor={DF_TILE} position={[0, 0.5, 0]} zIndexRange={[7, 0]} style={{ pointerEvents: "none" }}>
              <SpriteImg src={comp} gender={gender} size={ART_TILE} />
            </Html>
          </group>
        </>
      )}
      {/* just over the head — at 1.72 it read as a label on the trophy behind */}
      {name && <NamePlate text={name} color={tint} y={1.5} />}
    </group>
  );
}

function RemoteActor({ uid, meta, othersRef }) {
  const read = useMemo(() => () => {
    const d = othersRef.current?.[uid];
    return d ? { x: d.x || 0, y: d.y || 0, fx: d.facingX || 0, fy: d.facingY ?? 1 } : null;
  }, [uid, othersRef]);
  return <Actor read={read} gender={meta.gender} companionId={null} name={meta.name} tint="#a78bfa" />;
}

// the scene re-renders when the furnishing state changes; the people need not
const ActorM = React.memo(Actor);
const RemoteActorM = React.memo(RemoteActor);

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CAMERA RIG — THE CAMERA CONTRACT, all four pieces
// ────────────────────────────────────────────────────────────────────
// Pier3D's rig with this room's own framing: lower and a touch closer, so
// the default shot looks ACROSS the hall at the Wall of Renown instead of
// down on a floor plan. Tiles become world units through `S`.
// ════════════════════════════════════════════════════════════════════
const TMP_A = new THREE.Vector3();
const TMP_B = new THREE.Vector3();

function CameraRig({ posRef, glideRef, room, furnishing }) {
  const { camera, gl, size } = useThree();
  // ⭐ the build framing: a little higher while the shed is open, so the
  // tile you are aiming at is never under your own sprite (dial)
  const buildRef = useRef(furnishing);
  buildRef.current = furnishing;
  const controls = useRef();
  const lastTarget = useRef(new THREE.Vector3(posRef.current.x * S, 1, posRef.current.y * S));
  const lastReset = useRef(-1);                   // frame one frames the player
  // ⚑ THE ASPECT FIT — a portrait phone stands the lens further back
  const fit = size.width < size.height ? Math.min(1.5, 0.78 * (size.height / size.width)) : 1;
  const BASE_DIST = 8.6 * fit, BASE_HEIGHT = 3.7 * fit;

  // a yaw left over from here would silently rotate input in the 2D hall
  useEffect(() => () => resetGroundForward(), []);
  // ⚠ FREE IS A MODULE GLOBAL. Orbit on the pier, walk in, and the Hall stayed
  // FREE for the whole visit (no end-wall clamp, backing out into the dark);
  // walk back out and the pier stayed FREE, aimed at the Hall's front.
  // Every door is a fresh framing (code review).
  useEffect(() => { setPosed(false); return () => setPosed(false); }, []);
  // dev only: the preview reads the lens and the cut-away from here
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return undefined;
    window.__hallCam = camera; window.__hallCuts = CUTS; window.__hallGL = gl;
    return () => { delete window.__hallCam; delete window.__hallCuts; delete window.__hallGL; };
  }, [camera, gl]);

  useFrame((_, dt) => {
    const c = controls.current; if (!c) return;
    const gp = glideRef ? glideRef.current : { x: posRef.current.x * S, z: posRef.current.y * S };
    const tx = gp.x, ty = 1.0, tz = gp.z;
    // ⚑ the lens keeps off the end walls: at the west door it stands a few
    // units inside and looks back at you, so the end wall is a wall you see
    // at an angle, not a sheet of stone across half the frame
    const cx = room ? Math.min(Math.max(tx, room.xW + 3.4), room.xE - 3.4) : tx;
    // ⚑ and off the dark past the Gallery Wall: near the south rows it
    // stops backing away and rises instead, so the frame stays on the room
    const wantZ = tz + BASE_DIST * viewMult();
    const cz = Math.min(wantZ, room ? room.zS + 4.5 : Infinity);
    const lift = (wantZ - cz) * 0.4;

    // 4 · MOVEMENT FOLLOWS THE EYE — world z is tile y, and S is uniform
    camera.getWorldDirection(TMP_A);
    TMP_A.y = 0;
    if (TMP_A.lengthSq() > 1e-6) { TMP_A.normalize(); setGroundForward(TMP_A.x, TMP_A.z); }

    // 3 · DEFAULT is an ACTION, driven by a COUNTER
    const tok = resetToken();
    if (tok !== lastReset.current) {
      lastReset.current = tok;
      const m = viewMult();
      camera.position.set(cx, ty + BASE_HEIGHT * m + lift, cz);
      c.target.set(tx, ty, tz);
      lastTarget.current.set(tx, ty, tz);
      c.update();
    } else if (isPosed()) {
      // 2 · FREE POSE — translate the pose with the player, never re-aim
      TMP_B.set(tx - lastTarget.current.x, ty - lastTarget.current.y, tz - lastTarget.current.z);
      camera.position.add(TMP_B);
      c.target.set(tx, ty, tz);
      lastTarget.current.set(tx, ty, tz);
      c.update();
    } else {
      // 1 · the steered framing, goal-based
      const m = viewMult();
      const bh = buildRef.current ? 1.55 : 1;
      TMP_B.set(cx, ty + BASE_HEIGHT * m * bh + lift, cz);
      camera.position.lerp(TMP_B, 0.055);
      c.target.lerp(TMP_A.set(tx, ty, tz), 0.12);
      lastTarget.current.copy(c.target);
      c.update();
    }
    // the dollhouse: walls whose outside the lens stands on step aside
    driveCuts(camera, dt);
  });

  return (
    <OrbitControls
      ref={controls} makeDefault enablePan={false}
      onStart={() => setPosed(true)}
      minDistance={2.4} maxDistance={26}
      maxPolarAngle={Math.PI / 1.9}
      enableDamping dampingFactor={0.08}
      domElement={gl.domElement}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// THE FURNITURE — MineriumProp, exactly as the yard stands it
// ────────────────────────────────────────────────────────────────────
// ⭐ SCALED TO THE ROOM (2026-09-15). The kit is authored at one unit per
// yard tile; a Hall tile is S = 1.5 units, so unscaled furniture stood a
// third of a tile wide — a chair the selection outline framed like a
// coin on a plate. Not S itself: at 1.5× a chair's seat reached the
// sprite's chest. A dial between the two.
// ════════════════════════════════════════════════════════════════════
export const HALL_PROP_SCALE = 1.25;

// A floor outline, not a ring: the tiles a piece claims, drawn as a
// thin inlay on the marble with corner blocks. (OW-34: a rectangle — `w`
// along x and `d` along z, in world units, already turned.)
function FloorOutline({ w, d, color, opacity = 0.95 }) {
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ color, transparent: true, opacity, toneMapped: false, depthWrite: false }), [color, opacity]);
  useEffect(() => () => mat.dispose(), [mat]);
  const hx = w / 2, hz = d / 2, t = 0.05;
  return (
    <group position={[0, 0.02, 0]}>
      {[[0, -hz, w, t], [0, hz, w, t], [-hx, 0, t, d], [hx, 0, t, d]].map(([x, z, bw, bd], i) => (
        <mesh key={i} material={mat} position={[x, 0, z]}><boxGeometry args={[bw, 0.012, bd]} /></mesh>
      ))}
      {[[-hx, -hz], [hx, -hz], [-hx, hz], [hx, hz]].map(([x, z], i) => (
        <mesh key={`c${i}`} material={mat} position={[x, 0.01, z]}><boxGeometry args={[0.16, 0.03, 0.16]} /></mesh>
      ))}
    </group>
  );
}
// the outline for a piece at an angle, in world units
const outlineOf = (itemId, rot) => { const { w, d } = dimsOf(itemId, rot); return { w: w * S - 0.12, d: d * S - 0.12 }; };

const PropShadow = React.memo(function PropShadow({ w, d }) {
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, opacity: 0.7, depthWrite: false }), []);
  useEffect(() => () => mat.dispose(), [mat]);
  return <mesh material={mat} position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[0.9 * w + 0.5, 0.9 * d + 0.5]} /></mesh>;
});

// ⭐ OW-34 · A HANGING IS CARRIED TO THE WALL IT FACES. Its row stands on
// the floor tile in front of the wall; the model is drawn against z = 0
// facing +Z, so it moves back by half a tile, along the way it faces.
const WALL_INSET = S / 2 - 0.03;
const wallOffset = (rot) => [-Math.sin(rot || 0) * WALL_INSET, -Math.cos(rot || 0) * WALL_INSET];

// ⭐ AND IT STEPS ASIDE WITH ITS WALL. The cut-away hides a wall whose
// outside the lens stands on; a hanging left behind floats in the air.
// It is hidden, not faded — the prop kit's materials are shared by every
// piece in the room, so fading one would fade them all.
// ⚠ Hidden also means UNCLICKABLE: a hidden group still raycasts, so a
// cut-away tapestry could be tapped through thin air (review). The layer
// mask does both — the camera and the raycaster only see layer 0.
function WallHide({ room, rot, children }) {
  const g = useRef();
  const west = Math.abs(Math.sin(rot || 0)) > 0.5;
  useFrame(({ camera }) => {
    const o = g.current; if (!o || !room) return;
    const out = west ? camera.position.x < room.xW + 0.25 : camera.position.z < room.zN + 0.25;
    if (o.visible === out) {
      o.visible = !out;
      o.traverse((c) => c.layers.set(out ? 1 : 0));
    }
  });
  return <group ref={g}>{children}</group>;
}

const Placed = React.memo(function Placed({ propId, itemId, x, z, rot, lift, fill, ripe, selected, tapRef, hidden, flame, room }) {
  const layer = layerOf(itemId);
  const out = outlineOf(itemId, rot);
  const [ox, oz] = layer === LAYER.WALL ? wallOffset(rot) : [0, 0];
  // `e.delta` > 3 px is an orbit drag, not a tap; a hidden group still
  // raycasts, so the handler comes off while the piece is being moved
  const onClick = (e) => { if (e.delta > 3) return; e.stopPropagation(); tapRef?.current?.prop?.(propId, { x: e.point.x / S, y: e.point.z / S }); };
  const model = (
    <group position={[ox, lift * HALL_PROP_SCALE, oz]} rotation={[0, rot || 0, 0]} scale={HALL_PROP_SCALE}>
      <MineriumProp itemId={itemId} fill={fill} ripe={ripe} flame={flame} />
    </group>
  );
  return (
    <group position={[x, 0, z]} visible={!hidden} onClick={hidden ? undefined : onClick}>
      {lift === 0 && layer === LAYER.STAND && <PropShadow w={out.w / S + 0.1} d={out.d / S + 0.1} />}
      {layer === LAYER.WALL ? <WallHide room={room} rot={rot}>{model}</WallHide> : model}
      {selected && <FloorOutline w={out.w} d={out.d} color="#f0d060" />}
    </group>
  );
});

// ── THE BURSTS — +✹N rising off a collected grower ─────────────────
function Bursts({ bursts }) {
  return (bursts || []).map((b) => (
    <group key={b.id} position={[b.x * S, 1.2, b.y * S]}>
      <Html center distanceFactor={DF_TILE} zIndexRange={[9, 0]} style={{ pointerEvents: "none" }}>
        <div style={{ position: "relative", width: 1, height: 1 }}>
          <div style={burstStyle}>+✹{(b.amount || 0).toLocaleString()}</div>
        </div>
      </Html>
    </group>
  ));
}

// ── THE GRID — furnish mode only, over the walkable floor ───────────
function TileGrid({ layout: L }) {
  const layout = L.shell || L;      // the grid is the room's, not the walls'

  const geo = useMemo(() => {
    const pts = [];
    const x0 = (1 - 0.5) * S, x1 = (layout.cols - 2 + 0.5) * S;
    const z0 = (layout.floorTop - 0.5) * S, z1 = (layout.floorBot + 0.5) * S;
    for (let x = x0; x <= x1 + 1e-6; x += S) pts.push(x, 0, z0, x, 0, z1);
    for (let z = z0; z <= z1 + 1e-6; z += S) pts.push(x0, 0, z, x1, 0, z);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return g;
  }, [layout]);
  useEffect(() => () => geo.dispose(), [geo]);
  return (
    <lineSegments geometry={geo} position={[0, 0.02, 0]}>
      <lineBasicMaterial color={UI.gold} transparent opacity={0.18} depthWrite={false} toneMapped={false} />
    </lineSegments>
  );
}

// ── ⭐ OW-34 · THE BUILD HINTS — what the held piece may and may not use ──
// Holding a hanging, the floor tiles in front of hangable wall glow gold,
// with a bar toward their wall. Holding furniture, the tiles a deed or a
// fixture keeps for itself are dimmed red. One merged geometry per set,
// no per-frame cost, built once per layout.
function BuildHints({ layout, layer }) {
  const geo = useMemo(() => {
    const quads = [];
    const quad = (cx, cz, w, d) => {
      const x0 = cx - w / 2, x1 = cx + w / 2, z0 = cz - d / 2, z1 = cz + d / 2;
      quads.push(x0, 0, z0, x1, 0, z0, x1, 0, z1, x0, 0, z0, x1, 0, z1, x0, 0, z1);
    };
    if (layer === "wall") {
      for (const t of layout.wallSpots || []) {
        quad(t.x * S, t.y * S, S * 0.7, S * 0.7);
        if (t.side === "N") quad(t.x * S, (t.y - 0.45) * S, S * 0.86, S * 0.08);
        else quad((t.x - 0.45) * S, t.y * S, S * 0.08, S * 0.86);
      }
    } else if (layer === "stand") {
      for (const [x, y] of layout.reservedTiles || []) quad(x * S, y * S, S * 0.9, S * 0.9);
    }
    if (!quads.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(quads, 3));
    return g;
  }, [layout, layer]);
  useEffect(() => () => geo?.dispose(), [geo]);
  if (!geo) return null;
  return (
    <mesh geometry={geo} position={[0, 0.022, 0]} renderOrder={2}>
      <meshBasicMaterial color={layer === "wall" ? UI.goldHi || "#f0d060" : "#e8453c"} transparent
                         opacity={layer === "wall" ? 0.32 : 0.14} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  );
}

// ── ⭐ OW-42 · THE ARCHITECT'S CURSOR — the tile a tool will build on ──
// A glowing frame on the floor: green where it may build, red where not,
// violet while a deed is being carried. It slides; it never re-renders.
function TileCursor({ x, y, ok, deed }) {
  const g = useRef();
  const mat = useRef();
  const at = useRef({ x, y }); at.current.x = x; at.current.y = y;
  const init = useRef([x * S, 0.03, y * S]);
  useFrame(({ clock }) => {
    const o = g.current; if (!o) return;
    const tx = at.current.x * S, tz = at.current.y * S;
    if (Math.hypot(tx - o.position.x, tz - o.position.z) > 4) o.position.set(tx, 0.03, tz);
    else { o.position.x += (tx - o.position.x) * 0.4; o.position.z += (tz - o.position.z) * 0.4; }
    if (mat.current) mat.current.opacity = 0.5 + 0.25 * Math.sin(clock.elapsedTime * 5);
  });
  // violet while a deed is carried to a good spot; red wherever it cannot go
  const color = !ok ? "#e8453c" : deed ? "#b388ff" : "#9ad46a";
  return (
    <group ref={g} position={init.current} renderOrder={3}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[S * 0.44, S * 0.5, 4, 1, Math.PI / 4]} />
        <meshBasicMaterial ref={mat} color={color} transparent opacity={0.7} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[S * 0.66, S * 0.66]} />
        <meshBasicMaterial color={color} transparent opacity={0.16} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ── ⭐ OW-42 · DEED PICKERS — while the deed tool is out, every deed is a
// click target (the deeds themselves are merged meshes). Invisible boxes,
// mounted only for that tool.
const PICK_GEO = new THREE.BoxGeometry(1, 1, 1);
const PICK_MAT = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, colorWrite: false });
function DeedPickers({ layout, tapRef }) {
  const zN = roomOf(layout).zN;
  return layout.trophies.map((t) => {
    const wall = t.mount === "wall";
    return (
      <mesh key={t.id} geometry={PICK_GEO} material={PICK_MAT}
            position={wall ? [t.x * S, 2.0, zN + 0.2] : [t.x * S, 0.8, t.y * S - 0.4]}
            scale={wall ? [1.1, 1.4, 0.4] : [0.8, 1.6, 0.8]}
            onClick={(e) => { if (e.delta > 3) return; e.stopPropagation(); tapRef?.current?.deed?.(t.id); }} />
    );
  });
}

// ── ⭐ OW-42 · THE WALLS THAT SEAL A BROKEN HALL — drawn faded red in the
// Architect, so the owner can see what to knock down (they are not built)
const HIDDEN_MAT = new THREE.MeshBasicMaterial({ color: "#e8453c", transparent: true, opacity: 0.35, depthWrite: false, toneMapped: false });
function HiddenWalls({ hall }) {
  return [...(hall.hiddenWalls || []), ...(hall.hiddenDoors || [])].map((w) => (
    <mesh key={`${w.x},${w.y}`} geometry={PICK_GEO} material={HIDDEN_MAT} position={[w.x * S, 0.5, w.y * S]} scale={[S * 0.8, 1, S * 0.8]} />
  ));
}

// ── THE TAP PLANE — click a tile to aim the held piece ──────────────
// Invisible to the eye, not to the raycaster: an opacity-0 plane over the
// floor. `e.point` is world units; the screen wants Hall tiles.
function TapPlane({ layout, tapRef }) {
  const w = layout.cols * S, d = layout.rows * S;
  return (
    <mesh position={[w / 2, 0.005, d / 2]} rotation={[-Math.PI / 2, 0, 0]}
          onClick={(e) => { if (e.delta > 3) return; tapRef?.current?.ground?.(e.point.x / S, e.point.z / S); }}>
      <planeGeometry args={[w, d]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
    </mesh>
  );
}

// ⚑ OW-42: a rug lies OVER painted floor (paint sits at 0.017)
const FLOOR_LIFT = 0.006;
function PropsLayer({ hallProps, allProps, growers, selectedId, tapRef, movingPropId, flame, room }) {
  const [, bump] = useState(0);
  useEffect(() => {
    const t = setInterval(() => bump((v) => v + 1), 5000);   // ripeness is minutes-scale
    return () => clearInterval(t);
  }, []);
  const drawn = useMemo(() => (hallProps || []).map((p) => {
    const item = ITEMS[p.itemId];
    if (!item) return null;
    const at = resolvePlacement(allProps, p.propId) || { x: p.x, y: p.y, lift: 0, rot: p.rot };
    return { ...p, item, at };
  }).filter(Boolean), [hallProps, allProps]);
  const now = serverNow();
  return drawn.map((p) => {
    const g = p.item.kind === KIND.GROWER ? growthOf(p.item, growers?.[p.propId], now) : null;
    return (
      <Placed key={p.propId} propId={p.propId} tapRef={tapRef} hidden={movingPropId === p.propId}
              itemId={p.itemId} x={p.at.x * S} z={p.at.y * S}
              rot={p.at.rot} lift={(p.at.lift || 0) + (layerOf(p.itemId) === LAYER.FLOOR ? FLOOR_LIFT : 0)} fill={g?.fill || 0} ripe={!!g && g.cycles >= 1}
              selected={selectedId === p.propId} flame={flame} room={room} />
    );
  });
}

// ── THE GHOST — the piece you hold, where it will land ───────────────
// ⭐ It stands where the SCREEN says (`ghostAtRef`: the engine's
// `reachTile()`, or the tile you tapped) — one number for the ring, the
// prompt and the collision test. Three colours: ground / balances / no.
// The model is the real MineriumProp with its materials swapped for
// translucent copies; the shared yard materials are never touched.
function Ghost({ itemId, rot, state = "free", ghostAtRef, flame }) {
  const g = useRef();
  const model = useRef();
  const clones = useRef(new Map());
  const out = outlineOf(itemId, rot);
  const [ox, oz] = layerOf(itemId) === LAYER.WALL ? wallOffset(rot) : [0, 0];
  const tint = (GHOST_VERB[state] || GHOST_VERB.free).tint;
  const blocked = state === "blocked";
  const init = useRef([(ghostAtRef?.current?.x ?? 0) * S, 0, (ghostAtRef?.current?.y ?? 0) * S]);
  useEffect(() => () => { clones.current.forEach((c) => c.dispose()); clones.current.clear(); }, []);
  useFrame(() => {
    const o = g.current; if (!o) return;
    const at = ghostAtRef?.current; if (!at) return;
    if (Math.hypot(at.x * S - o.position.x, at.y * S - o.position.z) > 4) { o.position.x = at.x * S; o.position.z = at.y * S; return; }
    o.position.x += (at.x * S - o.position.x) * 0.35;
    o.position.z += (at.y * S - o.position.z) * 0.35;
    model.current?.traverse((m) => {
      if (!m.isMesh && !m.isPoints) return;
      const src = m.material;
      if (!src || Array.isArray(src)) return;
      let c = src.userData.hallGhost ? src : clones.current.get(src);
      if (!c) {
        c = src.clone();
        c.transparent = true; c.opacity = (src.opacity ?? 1) * 0.5; c.depthWrite = false;
        c.userData = { hallGhost: true };
        clones.current.set(src, c);
      }
      // green where it will stand, gold where it balances, red where it will not
      if (c.emissive) { c.emissive.set(tint); c.emissiveIntensity = blocked ? 0.5 : 0.35; }
      if (m.material !== c) m.material = c;
    });
  });
  return (
    <group ref={g} position={init.current}>
      <group ref={model} position={[ox, 0, oz]} rotation={[0, rot || 0, 0]} scale={HALL_PROP_SCALE}>
        <MineriumProp itemId={itemId} fill={0.5} flame={flame} />
      </group>
      <FloorOutline w={out.w} d={out.d} color={tint} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ OW-34 · THE MOOD — still the pier's THREE lights, only recoloured
// ────────────────────────────────────────────────────────────────────
// A worn light is colours and intensities for the same hemisphere, key and
// fill, and the fog. A risen one (Anima Bloom) breathes: the key and fill
// swell and lean toward its bloom colour on a slow sine. One useFrame, no
// new light, nothing allocated per frame.
// ════════════════════════════════════════════════════════════════════
const DEFAULT_MOOD = { hemi: ["#a89cb8", "#6a4a2c", 0.62], key: ["#ffdca4", 1.15], fill: [C.nullus, 0.42], fog: "#0e0a16" };
const _bloom = new THREE.Color();
function MoodLights({ mood }) {
  const m = mood || DEFAULT_MOOD;
  const hemi = useRef(), key = useRef(), fill = useRef();
  const base = useMemo(() => ({ key: new THREE.Color(m.key[0]), fill: new THREE.Color(m.fill[0]), bloom: new THREE.Color(m.bloom || m.key[0]) }), [m]);
  useFrame(({ clock }) => {
    if (!m.pulse) return;
    const k = 0.5 + 0.5 * Math.sin(clock.elapsedTime * 0.7);
    if (key.current) {
      key.current.intensity = m.key[1] * (0.88 + 0.24 * k);
      key.current.color.copy(base.key).lerp(_bloom.copy(base.bloom), 0.35 * k);
    }
    if (fill.current) {
      fill.current.intensity = m.fill[1] * (0.9 + 0.5 * k);
      fill.current.color.copy(base.fill).lerp(_bloom.copy(base.bloom), 0.5 * k);
    }
    if (hemi.current) hemi.current.intensity = m.hemi[2] * (0.94 + 0.12 * k);
  });
  // a mood that stops pulsing hands its lights back at rest
  useEffect(() => {
    if (key.current) { key.current.intensity = m.key[1]; key.current.color.set(m.key[0]); }
    if (fill.current) { fill.current.intensity = m.fill[1]; fill.current.color.set(m.fill[0]); }
    if (hemi.current) hemi.current.intensity = m.hemi[2];
  }, [m]);
  return (
    <>
      <hemisphereLight ref={hemi} args={[m.hemi[0], m.hemi[1], m.hemi[2]]} />
      <directionalLight ref={key} position={[18, 26, 12]} intensity={m.key[1]} color={m.key[0]} />
      <directionalLight ref={fill} position={[-22, 14, -18]} intensity={m.fill[1]} color={m.fill[0]} />
      <fog attach="fog" args={[m.fog || DEFAULT_MOOD.fog, m.fogNear ?? 18, m.fogFar ?? 62]} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE ATMOSPHERE — what a light puts in the AIR (caP'n, 2026-09-16:
// "very unique from each other, not just slightly different colors")
//   dust    — Candlelight: warm motes, lazily drifting
//   frost   — Moonlit Vigil: cold glints falling slowly, each winking alone
//   embers  — Ember Hour: sparks rising, flickering, dimming as they climb
//   petals  — Anima Bloom: pink petals falling and swaying
// ONE Points object, a cloud that travels with you (the Hall is 90 units
// long — spread across all of it, a few hundred motes would be nothing).
// Positions are updated in place in useFrame; nothing is allocated.
// ════════════════════════════════════════════════════════════════════
// ⚠ the cloud is centred BETWEEN you and the lens, deep enough to hold both
// (review: its edge sat 0.6 units in front of the camera and motes popped
// in the player's face), and anything outside the room is hidden
const ATMOS_BOX = { x: 13, y: 5.6, z: 12 };
const ATMOS_LEAD = 4.3;          // half the rig's resting distance
const ATMOS = {
  dust:   { size: 0.07, opacity: 0.55, vy: [0.02, 0.06],   sway: 0.12, blend: THREE.AdditiveBlending, tex: "dot" },
  frost:  { size: 0.06, opacity: 0.8,  vy: [-0.22, -0.1],  sway: 0.05, blend: THREE.AdditiveBlending, tex: "star", twinkle: true },
  embers: { size: 0.09, opacity: 0.9,  vy: [0.5, 1.1],     sway: 0.35, blend: THREE.AdditiveBlending, tex: "dot", fadeUp: true },
  // fadeUp — each ember dims as it climbs; twinkle — each glint winks alone
  petals: { size: 0.2,  opacity: 0.85, vy: [-0.38, -0.2],  sway: 0.6,  blend: THREE.NormalBlending,   tex: "petal" },
};
const moteTex = (kind) => canvasTexP(`hall-mote-${kind}`, 64, (ctx, W) => {
  if (kind === "petal") {
    ctx.translate(W / 2, W / 2); ctx.rotate(0.6);
    const g = ctx.createRadialGradient(0, 0, 2, 0, 0, W * 0.4);
    g.addColorStop(0, "rgba(255,255,255,1)"); g.addColorStop(1, "rgba(255,255,255,0.85)");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.ellipse(0, 0, W * 0.4, W * 0.2, 0, 0, 7); ctx.fill();
    ctx.strokeStyle = "rgba(200,120,160,0.6)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(-W * 0.35, 0); ctx.lineTo(W * 0.35, 0); ctx.stroke();
    return;
  }
  const g = ctx.createRadialGradient(W / 2, W / 2, 0, W / 2, W / 2, W / 2);
  g.addColorStop(0, "rgba(255,255,255,1)"); g.addColorStop(0.3, "rgba(255,255,255,0.6)"); g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, W);
  if (kind === "star") {
    ctx.strokeStyle = "rgba(255,255,255,0.9)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W / 2, 4); ctx.lineTo(W / 2, W - 4); ctx.moveTo(4, W / 2); ctx.lineTo(W - 4, W / 2); ctx.stroke();
  }
});
function Atmosphere({ atmos, glideRef, room }) {
  const kind = ATMOS[atmos?.kind] ? atmos.kind : null;
  const spec = kind ? ATMOS[kind] : null;
  const count = Math.min(240, atmos?.count || 100);
  const { geo, mat, seeds } = useMemo(() => {
    if (!spec) return {};
    const R = mulberry32(0xA7305 + count);
    const pos = new Float32Array(count * 3);
    const seeds = [];
    for (let i = 0; i < count; i++) {
      const s = { ox: (R() - 0.5) * 2 * ATMOS_BOX.x, oz: (R() - 0.5) * 2 * ATMOS_BOX.z, y: R() * ATMOS_BOX.y,
                  vy: spec.vy[0] + R() * (spec.vy[1] - spec.vy[0]), ph: R() * 6.28, fr: 0.4 + R() * 0.9 };
      seeds.push(s);
      pos.set([s.ox, s.y, s.oz], i * 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    // per-mote brightness rides on vertex colour (the tint × a factor)
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(count * 3).fill(1), 3));
    const m = new THREE.PointsMaterial({
      size: spec.size, map: moteTex(spec.tex), color: atmos.color || "#ffffff", transparent: true, vertexColors: true,
      opacity: spec.opacity, depthWrite: false, blending: spec.blend, sizeAttenuation: true, toneMapped: false,
    });
    return { geo: g, mat: m, seeds };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, count, atmos?.color]);
  useEffect(() => () => { geo?.dispose(); mat?.dispose(); }, [geo, mat]);
  const ref = useRef();
  useFrame(({ clock }, dt) => {
    const o = ref.current; if (!o || !seeds) return;
    const t = clock.elapsedTime, d = Math.min(dt, 0.05);
    const cx = glideRef.current.x, cz = glideRef.current.z + ATMOS_LEAD;
    const arr = geo.attributes.position.array;
    const col = geo.attributes.color.array;
    for (let i = 0; i < seeds.length; i++) {
      const s = seeds[i];
      s.y += s.vy * d;
      if (s.y > ATMOS_BOX.y) s.y -= ATMOS_BOX.y;
      if (s.y < 0) s.y += ATMOS_BOX.y;
      // each mote keeps its place in a cloud that follows you, wrapped
      let x = s.ox - (cx % (2 * ATMOS_BOX.x));
      x = ((x % (2 * ATMOS_BOX.x)) + 3 * ATMOS_BOX.x) % (2 * ATMOS_BOX.x) - ATMOS_BOX.x;
      let z = s.oz - (cz % (2 * ATMOS_BOX.z));
      z = ((z % (2 * ATMOS_BOX.z)) + 3 * ATMOS_BOX.z) % (2 * ATMOS_BOX.z) - ATMOS_BOX.z;
      const sw = Math.sin(t * s.fr + s.ph) * spec.sway;
      const wx = cx + x + sw, wz = cz + z + Math.cos(t * s.fr * 0.8 + s.ph) * spec.sway * 0.6;
      const inside = !room || (wx > room.xW && wx < room.xE && wz > room.zN && wz < room.zS);
      arr[i * 3] = wx;
      arr[i * 3 + 1] = inside ? s.y : -99;
      arr[i * 3 + 2] = wz;
      let k = 1;
      if (spec.fadeUp) k = Math.max(0, 1 - s.y / ATMOS_BOX.y) * (0.7 + 0.3 * Math.sin(t * 9 + s.ph));
      else if (spec.twinkle) k = 0.35 + 0.65 * Math.max(0, Math.sin(t * 2.2 * s.fr + s.ph * 3));
      col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = k;
    }
    geo.attributes.position.needsUpdate = true;
    geo.attributes.color.needsUpdate = true;
  });
  if (!spec) return null;
  return <points ref={ref} geometry={geo} material={mat} frustumCulled={false} renderOrder={5} />;
}

// ⚠ THE ROOM ITSELF IS MEMOISED. The scene re-renders whenever the tile you
// face gains or loses furniture or the ghost flips green/red; without this,
// every wall, deed and frame reconciled with it (code review).
const StaticHall = React.memo(function StaticHall({ layout, atlas, earned, isOwnHome, decorMap, galleryById, finish }) {
  return (
    <group>
      {/* ⚑ OW-42: the room is the SHELL (never changes); the deeds are the composed Hall's */}
      <HallShell
        L={layout.shell || layout}
        finish={finish}
        north={<WallDeeds L={layout} atlas={atlas} earned={earned} isOwnHome={isOwnHome} />}
        south={<GalleryFrames L={layout.shell || layout} decorMap={decorMap} galleryById={galleryById} />}
      />
      {layout.hall && <FloorPaint hall={layout.hall} />}
      <FloorDeeds L={layout} atlas={atlas} earned={earned} isOwnHome={isOwnHome} />
      <DeedBook L={layout.shell || layout} />
    </group>
  );
});

// ════════════════════════════════════════════════════════════════════
// THE SCENE
// ════════════════════════════════════════════════════════════════════
function Scene({
  layout, posRef, facingRef, gender, companionId, playerName, roster, othersRef,
  isOwnHome, earned, decorMap, galleryById, hallProps, allProps, growers,
  selectedId, ghostItemId, ghostRot, ghostState, ghostAtRef, tapRef, bursts, furnishing, movingPropId, finish, mood,
  wallsDown = false, archCursor = null, deedPick = false,
}) {
  const fontGen = useFontGen();
  // ⚑ the atlas is drawn from the SHELL's deeds (their order never changes),
  // so moving a deed or building a wall never repaints it
  const shell = layout.shell || layout;
  const atlas = useMemo(
    () => deedAtlas(shell.trophies, earned.key, earned.has, (t, e) => shell.deedInfo(t.id, e)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shell, earned.key, fontGen],
  );
  // ⚠ one atlas canvas per earned set: drop the old one when the set changes,
  // or every Hall visited and every deed earned kept a canvas alive (review)
  useEffect(() => () => {
    atlas.tex.dispose();
    for (const [k, t] of TEX) if (t === atlas.tex) TEX.delete(k);
  }, [atlas]);
  const readLocal = useMemo(() => () => ({
    x: posRef.current.x, y: posRef.current.y, fx: facingRef.current?.x || 0, fy: facingRef.current?.y || 0,
  }), [posRef, facingRef]);
  // ⚠ THE GLIDED POSITION, in world units, written by the local actor every
  // frame. The camera, the fader and the cut-away follow THIS, not the tile:
  // posRef jumps a whole tile per step, and a free camera driven by it jerked
  // 1.5 units in one frame while the sprite glided (code review, confirmed).
  const glideRef = useRef({ x: posRef.current.x * S, z: posRef.current.y * S });
  // the BuildDriver's fader reads a position in WORLD units, as { x, y }
  const worldPos = useRef({ x: glideRef.current.x, y: glideRef.current.z });
  useFrame(() => { worldPos.current.x = glideRef.current.x; worldPos.current.y = glideRef.current.z; });
  const room = useMemo(() => roomOf(layout), [layout]);

  return (
    <>
      {/* ⚑ FIXED LIGHT COUNT — THE PIER'S THREE, recoloured by the worn mood (OW-34) */}
      <MoodLights mood={mood} />
      <Atmosphere atmos={(mood || DEFAULT_MOOD).atmos} glideRef={glideRef} room={room} />

      <StaticHall key={`hall-${fontGen}`} layout={layout} atlas={atlas} earned={earned}
                  isOwnHome={isOwnHome} decorMap={decorMap} galleryById={galleryById} finish={finish} />

      {layout.hall && <InteriorWalls hall={layout.hall} finish={finish} glideRef={glideRef} wallsDown={wallsDown} tapRef={furnishing ? tapRef : null} />}
      {wallsDown && layout.hall?.broken && <HiddenWalls hall={layout.hall} />}

      {furnishing && <TileGrid layout={layout} />}
      {furnishing && archCursor && <TileCursor x={archCursor.x} y={archCursor.y} ok={archCursor.ok} deed={archCursor.deed} />}
      {furnishing && deedPick && <DeedPickers layout={layout} tapRef={tapRef} />}
      {furnishing && ghostItemId && <BuildHints layout={layout} layer={layerOf(ghostItemId)} />}
      {furnishing && <TapPlane layout={layout} tapRef={tapRef} />}
      <PropsLayer hallProps={hallProps} allProps={allProps} growers={growers} selectedId={selectedId}
                  tapRef={tapRef} movingPropId={movingPropId} flame={finish?.flameTint || undefined} room={room} />
      <Bursts bursts={bursts} />
      {ghostItemId && (
        <Ghost key={ghostItemId} itemId={ghostItemId} rot={ghostRot} state={ghostState} ghostAtRef={ghostAtRef} flame={finish?.flameTint || undefined} />
      )}

      <ActorM read={readLocal} gender={gender} companionId={companionId} name={playerName} tint="#f0d060" isLocal outRef={glideRef} />
      {roster.map((o) => <RemoteActorM key={o.uid} uid={o.uid} meta={o} othersRef={othersRef} />)}

      <BuildDriver posRef={worldPos} />
      <CameraRig posRef={posRef} glideRef={glideRef} room={room} furnishing={furnishing} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE HOST — React.memo, and its props must stay stable (see PierHome)
// ════════════════════════════════════════════════════════════════════
function Hall3DImpl(props) {
  const { layout, posRef, othersRef, rosterKey } = props;
  useEffect(() => () => { releaseTextures(); releasePropMaterials(); }, []);
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((v) => v + 1)), []);

  const roster = useMemo(() => Object.entries(othersRef?.current || {})
    .filter(([, d]) => !!d)
    .slice(0, 8)
    .map(([uid, d]) => ({ uid, gender: d.gender, name: d.name })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rosterKey, othersRef]);

  const view = getView();
  const posed = isPosed();
  const sx = posRef.current.x * S, sz = posRef.current.y * S;

  return (
    // ⚠ zIndex 0 makes this a stacking context, so the camera bar (z 520) stays
    // UNDER PierHome's modals (z 200+) instead of floating over them (review)
    <div style={{ position: "absolute", inset: 0, zIndex: 0, background: "#07050c" }}>
      <Canvas
        dpr={[1, 1.5]}
        shadows={false}
        camera={{ fov: 55, near: 0.2, far: 400, position: [sx, 4.7, sz + 8.6] }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        onCreated={({ gl }) => { gl.setClearColor("#07050c"); }}
      >
        <Scene {...props} roster={roster} layout={layout} />
      </Canvas>
      <CameraBar view={view} posed={posed} force={force} />
    </div>
  );
}

export default React.memo(Hall3DImpl);
